import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import {
  inputBaseClasses,
  ScopedCssBaseline,
  ThemeProvider,
  styled as muiStyled,
} from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  ScheduledBriefBizReview as ScheduledBrief,
  CoauthorSummary,
  type CoauthorSummaryDTO,
  type CoauthorSummaryProps,
  buildRbrSummariesGenerateRequestBody,
  mergeRbrSummariesPublishPayload,
  toRbrSummariesApiFilters,
} from '@workboard/ui-summary';
import type {
  ErrorToastEvent,
  SuccessToastEvent,
} from '@workboard/ui-okr/lib/internal/http/toast-helper';

import { GlobalStyle, theme } from './theme/theme';

const isEnabledRbrSummarySB = window?.wbConfig?.isEnabledRbrSummarySB ?? false;

type TCoauthorSummaryResponse = Omit<CoauthorSummaryDTO, 'updatedAt'> & {
  modifiedAt: string;
  publishedAt: string;
};

const convertToUnixTimestamp = (timestamp: string) => (Number(timestamp) / 1000).toFixed();

const convertToSummaryDTO = (data: TCoauthorSummaryResponse): CoauthorSummaryDTO => {
  const summary: CoauthorSummaryDTO = {
    ...data,
    createdAt: convertToUnixTimestamp(data.createdAt),
    updatedAt: convertToUnixTimestamp(data.modifiedAt),
  };

  if (data.publishedAt) {
    summary.publishedAt = convertToUnixTimestamp(data.publishedAt);
  }

  return summary;
};

const StyledCssBaseline = muiStyled(ScopedCssBaseline)({
  backgroundColor: 'transparent',
  'body .ui-widget &': { // workaround for conflicts with Direct & Dotted page CSS
    '& button': {
      fontSize: 16,
    },
    [`& textarea.${inputBaseClasses.input}`]: {
      '&, &::placeholder': {
        fontSize: 16,
      },
    },
  },
});

const StyledSummary = styled(isEnabledRbrSummarySB ? ScheduledBrief : CoauthorSummary)`
  > article {
    p {
      margin-bottom: 0;
    }
  }

  .MuiTextField-root {
    .MuiOutlinedInput-root {
      border: none;
      line-height: 150%;
      letter-spacing: 0.17px;
      padding: 8px 0px 16px;
    } 
    .MuiInputBase-adornedEnd {
      border: 1px solid rgba(0,0,0,0.23) !important;
      padding: 4px 10px !important;
    }
  }
`;

export interface IRbrSummaryProps {
  id: number;
  hideLanguageFilter: boolean;
  isReadOnly: boolean
  isDisabledGenSummaryButton: boolean;
  currentUser: Record<string, any>;
  onInsert: ({ header, summary }: { header: string; summary: CoauthorSummaryDTO }) => void;
  onToast: (event: SuccessToastEvent | ErrorToastEvent) => void;
  hideHeaderLeft: boolean;
  hideFocusFilter: boolean;

}

export const RbrSummaryGenAi = ({
  id,
  isReadOnly,
  hideLanguageFilter = true,
  isDisabledGenSummaryButton,
  currentUser,
  onInsert = () => null,
  onToast = () => null,
  hideHeaderLeft = true,
  hideFocusFilter = false,
}: IRbrSummaryProps) => {
  const summariesUrl = `${window?.wbConfig.baseUrl}/vnext/rbr/v1/summaries`;

  const YII_CSRF_TOKEN = window.getYiiToken();
  const fetchHeaders = useMemo(() => ({ 'X-Requested-With': 'XMLHttpRequest' }), []);
  const fetchDataHeaders = {
    ...fetchHeaders,
    'Content-Type': 'application/json',
    'yii-csrf-token': YII_CSRF_TOKEN,
  };

  const onGetSummary: CoauthorSummaryProps['onGetSummary'] = useCallback(
    async (params) => {
      // Merge API-oriented filters so GET …/last matches POST/PUT (format 4 + genAiBriefCustomId).
      const merged = { ...params, ...toRbrSummariesApiFilters(params) };
      const p = Object.fromEntries(
        Object.entries(merged)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)]),
      );
      let summary;
      if (isEnabledRbrSummarySB && p.resourceType !== 'bizreview') {
        summary = null;
      } else {
        const queryParams = new URLSearchParams({ isPublished: 'true', ...p });
        const response = await fetch(
          `${summariesUrl}/last?${queryParams.toString()}`,
          { headers: fetchHeaders },
        );

        if (response.ok) {
          summary = await response.json();
        } else {
          summary = null;
        }
      }
      return summary ? convertToSummaryDTO(summary) : summary;
    },
    [fetchHeaders, summariesUrl],
  );

  const onGenerateSummary: CoauthorSummaryProps['onGenerateSummary'] = async (data) => {
    if (isDisabledGenSummaryButton) {
      return {} as CoauthorSummaryDTO;
    }

    const body = buildRbrSummariesGenerateRequestBody(data, {
      resourceId: id,
      resourceType: 'bizreview',
    });

    const response = await fetch(
      summariesUrl,
      {
        method: 'POST',
        headers: fetchDataHeaders,
        body: JSON.stringify(body),
      },
    );

    if (response.ok) {
      const summary = await response.json();

      return convertToSummaryDTO(summary);
    }

    // read error message from data. If not found, read from message
    const { message, data: errorData } = await response.json();
    const errorSummary = errorData?.summary as Array<{ [key: string]: Record<string, unknown> }>;
    const mode = errorData?.mode as string;
    if (errorSummary?.length) {
      errorSummary
        ?.filter((value) => value?.[mode])
        ?.forEach((value) => {
          const block = value?.[mode] as { status_code?: number; summary?: unknown };
          const statusCode = block?.status_code;
          if (statusCode && (statusCode === 204 || (statusCode >= 400 && statusCode < 600))) {
            throw new Error(block?.summary as string);
          }
        });
    }
    throw new Error(message);
  };

  const onPublishSummary: CoauthorSummaryProps['onPublishSummary'] = async (_, data) => {
    if (!data?.id) {
      const event = {
        toastType: 'error',
        message: 'Oops something went wrong. Please try again.',
        error: new Error('Summary ID is missing'),
      } as ErrorToastEvent;

      onToast(event);

      return {} as CoauthorSummaryDTO;
    }

    const publishPayload = mergeRbrSummariesPublishPayload({
      ...data,
      format: data?.format ?? 1,
    });

    const response = await fetch(
      `${summariesUrl}/${data?.id ?? null}`,
      {
        method: 'PUT',
        headers: fetchDataHeaders,
        body: JSON.stringify(publishPayload),
      },
    );

    if (response.ok) {
      const summary = await response.json();

      return convertToSummaryDTO(summary);
    }

    const error = await response.json();

    throw new Error(error.message);
  };

  useEffect(() => {
    if (isDisabledGenSummaryButton) {
      return;
    }

    onGetSummary({
      resourceId: id,
      resourceType: 'bizreview'
    });
  }, [id, isDisabledGenSummaryButton, onGetSummary])

  return (
    <StyledCssBaseline>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <StyledSummary
          id={id}
          type="bizreview"
          title="Biz Review Brief"
          currentUser={currentUser}
          hideLanguageFilter={hideLanguageFilter}
          hidePublishIcon={isReadOnly}
          hideInsertIcon={isReadOnly}
          isDisabledGenSummaryButton={isDisabledGenSummaryButton}
          onGetSummary={onGetSummary}
          onGenerateSummary={onGenerateSummary}
          onPublishSummary={onPublishSummary}
          onInsertClick={onInsert}
          onToast={onToast}
          resourceId={id}
          hideHeaderLeft={hideHeaderLeft}
          hideFocusFilter={hideFocusFilter}
        />
      </ThemeProvider>
    </StyledCssBaseline>
  );
};
